<ion-header>
  <div class="modal-header">
    <ion-toolbar>
      <div class="modal-title">
        <h3>Create Escalation</h3>
      </div>
      <ion-buttons slot="end">
        <ion-button
          fill="clear"
          shape="round"
          class="close-button"
          (click)="dismiss()"
        >
          <ion-icon name="close"></ion-icon>
        </ion-button>
      </ion-buttons>
    </ion-toolbar>
  </div>

  <div style="padding: 0 15px 15px">
    <div
      *ngIf="conversation?.friendlyId"
      class="help-text"
      style="margin-bottom: 16px"
    >
      Escalating conversation #{{ conversation?.friendlyId }}
    </div>
    <div>
      <ion-label class="input-label">
        Escalation type <span class="required-indicator">*</span>
      </ion-label>
      <div>
        <ion-chip
          mode="ios"
          class="select-chip select-outline no-margin input-field full-width"
          [outline]="true"
          (click)="showEscalationTypeOptions($event)"
          [disabled]="isLoadingEscalationTypes || escalationTypes.length === 0"
          style="justify-content: space-between"
        >
          <ion-label>
            <ng-container
              *ngIf="isLoadingEscalationTypes; else escalationTypeContent"
            >
              <ion-spinner
                name="crescent"
                style="width: 16px; height: 16px; margin-right: 8px"
              ></ion-spinner>
              Loading...
            </ng-container>
            <ng-template #escalationTypeContent>
              <ng-container
                *ngIf="escalationTypes.length > 0; else noEscalationTypes"
              >
                {{ selectedEscalationType?.name ?? "None" }}
              </ng-container>
              <ng-template #noEscalationTypes>
                <div
                  style="
                    display: flex;
                    align-items: center;
                    color: var(--ion-color-medium);
                  "
                >
                  <ion-icon
                    name="alert-circle-outline"
                    style="margin-right: 8px"
                  ></ion-icon>
                  No escalation paths configured
                </div>
              </ng-template>
            </ng-template>
          </ion-label>
          <ion-icon
            *ngIf="escalationTypes.length > 0"
            name="chevron-down-outline"
            class="button-icon dropdown-icon"
          ></ion-icon>
        </ion-chip>
      </div>
      <div
        *ngIf="!isLoadingEscalationTypes && escalationTypes.length === 0"
        class="help-text"
        style="margin-top: 8px"
      >
        <p>
          You haven't set up any escalation paths yet. Please configure
          escalation types in your project settings.
        </p>
      </div>
    </div>

    <!-- Only show form fields when escalation types are available and not loading -->
    <ng-container
      *ngIf="!isLoadingEscalationTypes && escalationTypes.length > 0"
    >
      <div *ngIf="showAssigneeField">
        <ion-label class="input-label">
          Assignee
          <span *ngIf="isAssigneeRequired" class="required-indicator">*</span>
          <span *ngIf="!isAssigneeRequired" class="optional-indicator"
            >(optional)</span
          >
        </ion-label>
        <div>
          <ion-chip
            mode="ios"
            class="select-chip select-outline no-margin input-field full-width"
            [outline]="true"
            (click)="showAssigneeOptions($event)"
            [disabled]="!selectedEscalationType"
            style="justify-content: space-between"
          >
            <ion-label>
              {{ assignedToName ?? "None" }}
            </ion-label>
            <ion-icon
              name="chevron-down-outline"
              class="icon-padded-left button-icon dropdown-icon"
            ></ion-icon>
          </ion-chip>
        </div>
      </div>
      <div *ngIf="showPriorityField">
        <ion-label class="input-label">
          Priority
          <span *ngIf="isPriorityRequired" class="required-indicator">*</span>
          <span *ngIf="!isPriorityRequired" class="optional-indicator"
            >(optional)</span
          >
        </ion-label>
        <div>
          <ion-chip
            mode="ios"
            class="select-chip select-outline no-margin input-field full-width"
            [outline]="true"
            (click)="showPriorityOptions($event)"
            [disabled]="!selectedEscalationType"
            style="justify-content: space-between"
          >
            <ion-label>
              <ng-container
                *ngIf="
                  getPriorityEmoji() | colonToEmoji as emojiString;
                  else noPriority
                "
              >
                <img
                  *ngIf="isUrl(emojiString)"
                  [src]="emojiString"
                  alt="Priority emoji"
                  style="
                    width: 1em;
                    height: 1em;
                    vertical-align: -0.125em;
                    margin-right: 4px;
                  "
                />
                <span *ngIf="!isUrl(emojiString)" style="margin-right: 4px">{{
                  emojiString
                }}</span>
                {{ getPriorityLabel() }}
              </ng-container>
              <ng-template #noPriority>None</ng-template>
            </ion-label>
            <ion-icon
              name="chevron-down-outline"
              class="icon-padded-left button-icon dropdown-icon"
            ></ion-icon>
          </ion-chip>
        </div>
      </div>
      <div>
        <ion-label class="input-label">
          Subject
          <span class="required-indicator">*</span>
        </ion-label>
        <ion-input
          [(ngModel)]="title"
          placeholder="Brief description..."
          class="input-field"
        ></ion-input>
      </div>

      <div>
        <ion-label class="input-label">
          Additional details
          <span class="optional-indicator">(optional)</span>
        </ion-label>
        <ion-textarea
          #bodyInput
          lines="6"
          autoGrow="true"
          placeholder="Add more details to share with the team..."
          [(ngModel)]="body"
          class="input-field"
        ></ion-textarea>
      </div>

      <div>
        <ion-label class="input-label">
          Public note
          <span class="optional-indicator">(optional)</span>
        </ion-label>
        <ion-textarea
          #publicNoteInput
          lines="6"
          autoGrow="true"
          placeholder="Note to include in customer message..."
          [(ngModel)]="publicNote"
          class="input-field"
        ></ion-textarea>
      </div>

      <ion-button
        [disabled]="isLoading || !canSubmit()"
        (click)="submit()"
        size="block"
        style="margin: 10px 0 0 0"
      >
        Escalate
        <span *ngIf="selectedEscalationType?.slackChannel?.name">
          &nbsp;to #{{ selectedEscalationType?.slackChannel?.name }}
        </span>
        <ion-spinner
          name="crescent"
          [hidden]="!isLoading"
          class="submit-spinner"
        ></ion-spinner>
        <ion-icon
          name="arrow-forward-outline"
          class="icon-padded-left button-icon"
          [hidden]="isLoading"
        ></ion-icon>
      </ion-button>
    </ng-container>
  </div>
</ion-header>
